/**
 * Audio (Phase 10): UI sounds and an ambient soundscape per industry family,
 * all synthesised with the Web Audio API (no sound files to download).
 *
 *   sfx(kind)              tap | good | bad | soft | coin | month | moment | win | lose | board | bell
 *   setAmbientGroup(g)     digital | industrial | consumer | resources | services | care
 *   mountSoundControl(el)  the top-bar button and its little panel
 *
 * Settings live per browser (localStorage "ges.audio"): effects on/off,
 * ambient on/off, volume. Defaults: muted on phones and touch devices;
 * on desktop effects on, ambient off. Browsers only allow sound after a
 * user gesture, so the audio context starts on the first click.
 */
const AU_KEY = "ges.audio";
const AU_BROWSER = typeof window !== "undefined" && typeof document !== "undefined" && typeof document.addEventListener === "function";
const AU_MOBILE = () => AU_BROWSER && !!(window.matchMedia && (window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(max-width: 760px)").matches));

let auCtx = null, auMaster = null, auAmbient = null, auGroup = null;
const auSettings = (() => {
  const def = AU_MOBILE() ? { sfx: false, ambient: false, volume: 0.5 } : { sfx: true, ambient: false, volume: 0.5 };
  try { return { ...def, ...JSON.parse((typeof localStorage !== "undefined" && localStorage.getItem(AU_KEY)) || "{}") }; } catch (e) { return def; }
})();

function auSave() {
  try { localStorage.setItem(AU_KEY, JSON.stringify(auSettings)); } catch (e) { /* not remembered */ }
}

function auContext() {
  if (auCtx) return auCtx;
  const AC = AU_BROWSER && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  auCtx = new AC();
  auMaster = auCtx.createGain();
  auMaster.gain.value = auSettings.volume;
  auMaster.connect(auCtx.destination);
  return auCtx;
}

/** One enveloped tone. */
function auTone(freq, start, dur, { type = "sine", gain = 0.2, slide = 0 } = {}) {
  const c = auCtx, o = c.createOscillator(), g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq * slide), start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(auMaster);
  o.start(start);
  o.stop(start + dur + 0.05);
}

/** A short filtered noise burst (whoosh). */
function auNoise(start, dur, { gain = 0.15, from = 400, to = 3000 } = {}) {
  const c = auCtx, len = Math.ceil(c.sampleRate * dur), buf = c.createBuffer(1, len, c.sampleRate), d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
  src.buffer = buf;
  f.type = "bandpass";
  f.frequency.setValueAtTime(from, start);
  f.frequency.exponentialRampToValueAtTime(to, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + dur * 0.4);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(f).connect(g).connect(auMaster);
  src.start(start);
}

const AU_SFX = {
  tap: (t) => auTone(880, t, 0.06, { type: "triangle", gain: 0.06 }),
  soft: (t) => auTone(660, t, 0.12, { gain: 0.07 }),
  good: (t) => { auTone(660, t, 0.14, { gain: 0.12 }); auTone(990, t + 0.09, 0.2, { gain: 0.1 }); },
  bad: (t) => { auTone(220, t, 0.22, { type: "sawtooth", gain: 0.06, slide: 0.7 }); },
  coin: (t) => { auTone(1318, t, 0.08, { type: "square", gain: 0.05 }); auTone(1760, t + 0.07, 0.2, { type: "square", gain: 0.05 }); },
  month: (t) => { auNoise(t, 0.6, { gain: 0.12 }); auTone(392, t + 0.35, 0.4, { gain: 0.06 }); },
  moment: (t) => { [262, 330, 392].forEach((f, i) => auTone(f, t + i * 0.12, 0.5, { type: "triangle", gain: 0.08 })); },
  win: (t) => { [523, 659, 784, 1046].forEach((f, i) => auTone(f, t + i * 0.1, 0.45, { type: "triangle", gain: 0.1 })); },
  lose: (t) => { auTone(196, t, 0.5, { type: "sawtooth", gain: 0.07, slide: 0.6 }); auTone(147, t + 0.2, 0.6, { gain: 0.08 }); },
  board: (t) => { [0, 0.22, 0.44].forEach((d) => auTone(440, t + d, 0.08, { type: "square", gain: 0.04 })); },
  bell: (t) => { [0, 0.5, 1.0].forEach((d) => { auTone(880, t + d, 1.2, { gain: 0.12 }); auTone(1320, t + d, 0.9, { gain: 0.05 }); }); },
};

function sfx(kind) {
  if (!auSettings.sfx || !AU_SFX[kind]) return;
  const c = auContext();
  if (!c || c.state !== "running") return;
  try { AU_SFX[kind](c.currentTime + 0.01); } catch (e) { /* sound is optional */ }
}

// Ambient: a slow, quiet pad whose root, colour and texture follow the industry family.
const AU_AMBIENT = {
  digital: { notes: [220, 277.2, 329.6], type: "sine", cutoff: 1800, noise: 0.004 },
  industrial: { notes: [55, 82.4, 110], type: "sawtooth", cutoff: 420, noise: 0.02 },
  consumer: { notes: [196, 246.9, 293.7], type: "triangle", cutoff: 1400, noise: 0.006 },
  resources: { notes: [41.2, 61.7, 82.4], type: "sawtooth", cutoff: 300, noise: 0.03 },
  services: { notes: [174.6, 220, 261.6], type: "triangle", cutoff: 1100, noise: 0.004 },
  care: { notes: [261.6, 329.6, 392], type: "sine", cutoff: 2200, noise: 0.002 },
};

function auStopAmbient() {
  if (!auAmbient) return;
  const { nodes, gain } = auAmbient;
  const t = auCtx.currentTime;
  gain.gain.cancelScheduledValues(t);
  gain.gain.setTargetAtTime(0.0001, t, 0.6);
  setTimeout(() => nodes.forEach((n) => { try { n.stop(); } catch (e) { /* already stopped */ } }), 2500);
  auAmbient = null;
}

function auStartAmbient() {
  const c = auContext();
  if (!c || c.state !== "running" || !auSettings.ambient || auAmbient) return;
  const spec = AU_AMBIENT[auGroup] || AU_AMBIENT.digital;
  const gain = c.createGain(), filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = spec.cutoff;
  gain.gain.value = 0.0001;
  filter.connect(gain).connect(auMaster);
  const nodes = [];
  spec.notes.forEach((f, i) => {
    [0, 3].forEach((cents) => {
      const o = c.createOscillator(), g = c.createGain();
      o.type = spec.type;
      o.frequency.value = f;
      o.detune.value = cents + i * 2;
      g.gain.value = 0.05 / spec.notes.length;
      o.connect(g).connect(filter);
      o.start();
      nodes.push(o);
    });
  });
  const lfo = c.createOscillator(), lfoGain = c.createGain();
  lfo.frequency.value = 0.07;
  lfoGain.gain.value = spec.cutoff * 0.35;
  lfo.connect(lfoGain).connect(filter.frequency);
  lfo.start();
  nodes.push(lfo);
  if (spec.noise) {
    const len = c.sampleRate * 2, buf = c.createBuffer(1, len, c.sampleRate), d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(), ng = c.createGain();
    src.buffer = buf; src.loop = true;
    ng.gain.value = spec.noise;
    src.connect(ng).connect(filter);
    src.start();
    nodes.push(src);
  }
  gain.gain.setTargetAtTime(0.5, c.currentTime, 2.5);
  auAmbient = { nodes, gain };
}

function setAmbientGroup(group) {
  if (group === auGroup) return;
  auGroup = group;
  if (auAmbient) { auStopAmbient(); setTimeout(auStartAmbient, 400); } else auStartAmbient();
}

function auApply() {
  if (auMaster) auMaster.gain.setTargetAtTime(auSettings.volume, auCtx.currentTime, 0.05);
  if (auSettings.ambient) auStartAmbient(); else if (auAmbient) auStopAmbient();
  document.querySelectorAll("[data-au-icon]").forEach((el) => { el.textContent = auSettings.sfx || auSettings.ambient ? "🔊" : "🔇"; });
}

/** The top-bar sound button and its panel. */
function mountSoundControl(btn) {
  if (!btn || btn.dataset.auMounted) return;
  btn.dataset.auMounted = "1";
  btn.innerHTML = `<span data-au-icon>${auSettings.sfx || auSettings.ambient ? "🔊" : "🔇"}</span>`;
  btn.setAttribute("aria-label", "Sound settings");
  const panel = document.createElement("div");
  panel.className = "au-panel hidden";
  panel.setAttribute("role", "dialog");
  panel.innerHTML = `<label><input type="checkbox" data-au="sfx"> Sound effects</label>
    <label><input type="checkbox" data-au="ambient"> Ambient sound <em>(per industry)</em></label>
    <label class="au-vol">Volume <input type="range" min="0" max="1" step="0.05" data-au="volume"></label>`;
  document.body.appendChild(panel);
  const sync = () => {
    panel.querySelector('[data-au="sfx"]').checked = auSettings.sfx;
    panel.querySelector('[data-au="ambient"]').checked = auSettings.ambient;
    panel.querySelector('[data-au="volume"]').value = auSettings.volume;
  };
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const r = btn.getBoundingClientRect();
    panel.style.top = `${r.bottom + 8}px`;
    panel.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
    sync();
    panel.classList.toggle("hidden");
  });
  panel.addEventListener("click", (e) => e.stopPropagation());
  panel.addEventListener("input", (e) => {
    const k = e.target.dataset.au;
    if (!k) return;
    auSettings[k] = k === "volume" ? +e.target.value : e.target.checked;
    auSave();
    auContext();
    if (auCtx && auCtx.state !== "running") auCtx.resume().then(auApply); else auApply();
    if (k === "sfx" && auSettings.sfx) sfx("good");
  });
  document.addEventListener("click", () => panel.classList.add("hidden"));
}

// The first gesture unlocks audio; main buttons tick.
if (AU_BROWSER) document.addEventListener("pointerdown", () => {
  if (!auSettings.sfx && !auSettings.ambient) return;
  const c = auContext();
  if (c && c.state !== "running") c.resume().then(auApply).catch(() => {});
}, { capture: true });
if (AU_BROWSER) document.addEventListener("click", (e) => {
  const b = e.target.closest && e.target.closest("button.primary");
  if (b && !b.disabled) sfx("tap");
}, { capture: true });

if (AU_BROWSER) window.gesSfx = sfx;

export { mountSoundControl, setAmbientGroup, sfx };
